// ---------------------------------------------------------------------------
// nativeVoice — thin wrapper around the Capacitor TextToSpeech plugin and
// KeepAwake plugin. On native iOS/Android we get real AVSpeechSynthesizer /
// Android TTS — louder, clearer, and crucially keeps speaking when the screen
// dims, where window.speechSynthesis on iOS WebView silently drops the
// utterance. On the web we fall back to window.speechSynthesis so dev preview
// still demos correctly.
//
// VOICE CHOICE: the navigation voice is the user's to pick (real-drive note:
// "you should be able to choose the voice"). listEnglishVoices() surfaces the
// installed voices; setChosenVoiceId() persists a choice. When no choice is
// made, we auto-pick the best available — premium > enhanced > default — and
// the smoothest-sounding ("nicer", "less choppy") voices are the premium /
// enhanced ones the user downloads in iOS Settings.
// ---------------------------------------------------------------------------

import { Capacitor } from "@capacitor/core";
import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { KeepAwake } from "@capacitor-community/keep-awake";

const isNative = (): boolean => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// localStorage key for the user's chosen voice (stable identifier).
const VOICE_ID_KEY = "nav.voiceId";

// Warm-sounding voice names — nudged up within a quality tier because they're
// commonly considered the most natural iOS voices.
const WARM_NAMES = ["ava", "allison", "samantha", "tom", "evan", "joelle", "nathan", "noelle", "zoe"];

/** A navigation voice the user can pick from. */
export interface PleasantVoice {
  /** Stable identifier (voice.identifier; falls back to the name). */
  id: string;
  /** Human-facing voice name. */
  name: string;
  /** "premium" | "enhanced" | "default" — premium/enhanced sound smoother. */
  quality: string;
  /** Index into the plugin's getSupportedVoices() array — used by speak(). */
  index: number;
  /** BCP-47 language tag. */
  language: string;
}

// Caches. cachedVoices avoids re-querying the voice list on every utterance;
// resolvedIndex caches which index we'll actually speak with. Both are
// invalidated when the user changes their voice choice.
let cachedVoices: PleasantVoice[] | null = null;
let resolvedIndex: number | null | undefined = undefined; // undefined = unresolved

function readChosenId(): string | null {
  try { return localStorage.getItem(VOICE_ID_KEY); } catch { return null; }
}

/** The user's chosen voice id, or null when on "Automatic". */
export function getChosenVoiceId(): string | null {
  return readChosenId();
}

/** Set (or clear, with null) the chosen voice. Invalidates the speak cache so
 *  the very next prompt uses the new voice. */
export function setChosenVoiceId(id: string | null): void {
  try {
    if (id) localStorage.setItem(VOICE_ID_KEY, id);
    else localStorage.removeItem(VOICE_ID_KEY);
  } catch {
    /* ignore quota */
  }
  resolvedIndex = undefined;
}

function qualityScore(v: PleasantVoice): number {
  let s = v.quality === "premium" ? 200 : v.quality === "enhanced" ? 100 : 0;
  if (WARM_NAMES.some((n) => v.name.toLowerCase().includes(n))) s += 10;
  if (v.language.toLowerCase() === "en-us") s += 5;
  return s;
}

/**
 * The installed English voices, best-quality first. Cached after the first
 * call. Empty on the web fallback or if the query fails — callers should
 * handle a zero-length list gracefully.
 */
export async function listEnglishVoices(): Promise<PleasantVoice[]> {
  // Only treat a NON-EMPTY result as cacheable. Real-drive bug: a user
  // opened the picker before installing any Enhanced voices, the empty
  // list got cached, and even after the voices were installed the picker
  // kept saying "no installed voices found" — because the cache never
  // invalidated. An empty list means "we haven't seen any yet"; we re-ask
  // each call until we actually get voices.
  if (cachedVoices && cachedVoices.length > 0) return cachedVoices;
  try {
    const result = await TextToSpeech.getSupportedVoices();
    const raw = (result?.voices ?? []) as Array<{
      name?: string; language?: string; quality?: string; identifier?: string;
    }>;
    const mapped: PleasantVoice[] = raw
      .map((v, index) => ({ v, index }))
      .filter(({ v }) => (v.language ?? "").toLowerCase().startsWith("en"))
      .map(({ v, index }) => ({
        id: v.identifier || v.name || `voice-${index}`,
        name: v.name || v.identifier || `Voice ${index + 1}`,
        quality: (v.quality ?? "default").toLowerCase(),
        index,
        language: v.language ?? "en",
      }));
    mapped.sort((a, b) => qualityScore(b) - qualityScore(a));
    // Only cache when we actually have voices to remember.
    if (mapped.length > 0) cachedVoices = mapped;
    return mapped;
  } catch {
    // Don't cache the failure either — try again next time.
    return [];
  }
}

/** Resolve which plugin voice index to speak with: the user's chosen voice
 *  when set and still installed, otherwise the best auto-pick (or null to
 *  let the OS use its own default). */
async function resolveVoiceIndex(): Promise<number | null> {
  if (resolvedIndex !== undefined) return resolvedIndex;
  if (!isNative()) { resolvedIndex = null; return null; }
  const voices = await listEnglishVoices();
  if (voices.length === 0) { resolvedIndex = null; return null; }

  const chosenId = readChosenId();
  if (chosenId) {
    const match = voices.find((v) => v.id === chosenId);
    if (match) { resolvedIndex = match.index; return resolvedIndex; }
    // Chosen voice no longer installed — fall through to auto-pick.
  }

  // Auto-pick: the list is best-first. Only override the OS default when the
  // top voice is genuinely better (premium/enhanced or a warm-named one).
  const best = voices[0];
  resolvedIndex = qualityScore(best) > 0 ? best.index : null;
  return resolvedIndex;
}

export interface SpeakOptions {
  /** 0..1 — speaking volume. */
  volume?: number;
  /** Spoken language tag, default "en-US". */
  lang?: string;
}

/** Speak a phrase. Cancels any in-flight utterance first so an "approach"
 *  announcement doesn't queue behind a stale "recalculating" line. Never
 *  throws — voice is decorative; mute is handled by the caller. */
export const speakNative = async (text: string, opts: SpeakOptions = {}): Promise<void> => {
  if (!text) return;
  const volume = Math.max(0, Math.min(1, opts.volume ?? 1));
  const lang = opts.lang ?? "en-US";
  if (isNative()) {
    try {
      await TextToSpeech.stop().catch(() => {});
      const voiceIdx = await resolveVoiceIndex();
      const speakArgs: Record<string, unknown> = {
        text,
        lang,
        // Warmer, slower, slightly higher — closer to "kind guide" than
        // "robotic GPS." The biggest lever on smoothness, though, is the
        // VOICE itself: an enhanced/premium voice sounds far less choppy
        // than a compact one. See listEnglishVoices + the VoicePicker.
        rate: 0.9,
        pitch: 1.05,
        volume,
        // "playback" plays at full volume and isn't silenced by the iOS
        // ring/silent switch — essential for navigation guidance.
        category: "playback",
      };
      if (voiceIdx != null) speakArgs.voice = voiceIdx;
      await TextToSpeech.speak(speakArgs as unknown as Parameters<typeof TextToSpeech.speak>[0]);
    } catch {
      /* fall through to web fallback below if native rejects */
    }
    return;
  }
  // Web fallback — for dev preview AND the browser PWA (no native shell).
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    // Chrome/Safari quietly drop into a paused state after periods of silence
    // (and on some engines, after each utterance). Resume before every cue so
    // turn announcements don't go silent mid-drive.
    window.speechSynthesis.resume();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1;
    u.pitch = 1;
    u.volume = volume;
    u.lang = lang;
    window.speechSynthesis.speak(u);
  } catch { /* noop */ }
};

/** Web only: "unlock" the browser speech engine from a user gesture. Browsers
 *  (especially iOS Safari, but also some desktop cases) silently drop the first
 *  utterance unless speech was kicked off inside a real tap. Call this from the
 *  "Start navigation" handler so the very first turn cue is actually audible.
 *  No-op on native (the OS TTS needs no unlock). */
export const primeWebSpeech = (): void => {
  if (isNative()) return;
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    // Some engines park themselves in a paused state until resumed.
    window.speechSynthesis.resume();
    // A near-silent warm-up utterance flips the engine into the "allowed to
    // speak" state for the rest of the session.
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    window.speechSynthesis.speak(u);
  } catch { /* noop */ }
};

/** Stop any in-flight speech immediately. Safe to call from anywhere. */
export const stopSpeaking = async (): Promise<void> => {
  if (isNative()) {
    try { await TextToSpeech.stop(); } catch { /* noop */ }
    return;
  }
  try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
};

/** Prevent the screen from sleeping. Use it on nav open; pair with
 *  releaseKeepAwake() on nav close. Idempotent on both sides. */
export const acquireKeepAwake = async (): Promise<void> => {
  if (isNative()) {
    try { await KeepAwake.keepAwake(); } catch { /* noop */ }
    return;
  }
  try {
    if (typeof navigator !== "undefined" && (navigator as Navigator & { wakeLock?: { request: (t: string) => Promise<unknown> } }).wakeLock) {
      const w = window as Window & { __pleasantWakeLock?: { release?: () => Promise<void> } | null };
      if (!w.__pleasantWakeLock) {
        const lock = await (navigator as Navigator & { wakeLock: { request: (t: string) => Promise<{ release?: () => Promise<void> }> } }).wakeLock.request("screen");
        w.__pleasantWakeLock = lock;
      }
    }
  } catch { /* noop */ }
};

export const releaseKeepAwake = async (): Promise<void> => {
  if (isNative()) {
    try { await KeepAwake.allowSleep(); } catch { /* noop */ }
    return;
  }
  try {
    const w = window as Window & { __pleasantWakeLock?: { release?: () => Promise<void> } | null };
    await w.__pleasantWakeLock?.release?.();
    w.__pleasantWakeLock = null;
  } catch { /* noop */ }
};
